import { useState, useRef, useEffect } from "react";
import type { SseRouteMessage, DayPlan } from "@/api/aiChat";
import { INITIAL_SHOW_COUNT, LAZY_LOAD_STEP } from "./train-card";

// ════════════════════════════════════════════════════
//  线路规划/行程卡片（绿色旅行主题 + 懒加载）
// ════════════════════════════════════════════════════

interface RouteQueryRaw {
  departure: string;
  destination: string;
  source?: string;
  days: number;
  message?: string;
  total_km: number;
  total_hours: number;
  route_path?: string[];
  itinerary?: {
    overview?: string;
    summary?: string;
    daily_summary?: string;
    days: DayPlan[];
    route_info?: {
      path?: string;
      total_km?: number;
      total_hours?: number;
      segments?: string;
    };
  };
  routes?: {
    name?: string;
    overview?: string;
    summary?: string;
    departure?: string;
    destination?: string;
    days?: number;
    total_km?: number;
    total_hours?: number;
    route_path?: string[];
    daily_plans: DayPlan[];
  }[];
}

/** 获取每日行程数据源 */
function getDays(data: any): DayPlan[] | undefined {
  if (data.itinerary?.days) return data.itinerary.days;
  if (data.routes?.[0]?.daily_plans) return data.routes[0].daily_plans;
  return undefined;
}

export default function RouteCard({ data }: { data: SseRouteMessage | RouteQueryRaw }) {
  const rd = ("data" in data && (data as SseRouteMessage).data)
    ? (data as SseRouteMessage).data
    : data as RouteQueryRaw;

  // ★ 备选方案切换（>=2 条时才显示切换器）
  const [routeIdx, setRouteIdx] = useState(0);
  const routePlans = rd.routes ?? [];
  const activePlan = routePlans[routeIdx];

  /** 当前生效的数据视图（选中备选方案时以其字段为准，缺失则回退顶层） */
  const view: RouteQueryRaw = activePlan
    ? {
        ...rd,
        departure: activePlan.departure || rd.departure,
        destination: activePlan.destination || rd.destination,
        days: activePlan.days ?? rd.days,
        total_km: activePlan.total_km ?? rd.total_km,
        total_hours: activePlan.total_hours ?? rd.total_hours,
        route_path: activePlan.route_path ?? rd.route_path,
        itinerary: { days: activePlan.daily_plans },
      }
    : rd;

  // ★ 概要信息（顶层优先，备选方案次之）
  const overview = rd.itinerary?.overview || activePlan?.overview || "";
  const summary = rd.itinerary?.summary || activePlan?.summary || "";
  const dailySummary = rd.itinerary?.daily_summary || "";
  const routeInfo = rd.itinerary?.route_info;

  const days = getDays(view);
  if (!days?.length) return null;

  // ★ 懒加载状态
  const [visibleCount, setVisibleCount] = useState(INITIAL_SHOW_COUNT);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const showDays = days.slice(0, visibleCount);
  const isAllLoaded = visibleCount >= days.length;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || isAllLoaded) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((prev) => Math.min(prev + LAZY_LOAD_STEP, days.length));
      },
      { rootMargin: "80px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isAllLoaded, days.length]);

  /** 格式化小时数 */
  function fmtHours(h: number): string {
    const hours = Math.floor(h);
    const mins = Math.round((h - hours) * 60);
    return `${hours}h${mins > 0 ? `${mins}m` : ""}`;
  }

  return (
    <div className="flex flex-col gap-0 rounded-xl w-full"
      style={{ background: "#FAFBFF", border: "1px solid #EEF0F6" }}
    >
      {/* 总标题栏 */}
      <div
        className="flex items-center gap-2 px-3.5 py-2.5 flex-wrap"
        style={{ background: "linear-gradient(135deg, #ECFDF5, #D1FAE5)", borderBottom: "1px solid #EEF0F6" }}
      >
        <i className="ri-route-line" style={{ color: "#059669", fontSize: 15 }} />
        <span className="text-[12px] font-semibold" style={{ color: "#333" }}>
          {view.departure} → {view.destination}
        </span>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono"
          style={{ background: "#A7F3D0", color: "#047857" }}
        >
          {view.days}天行程
        </span>
        {/* 统计标签 */}
        <span className="text-[9px] px-1.5 py-0.5 rounded flex-shrink-0"
          style={{ background: "#D1FAE5", color: "#047857" }}
        >
          <i className="ri-map-pin-time-line" /> {Math.round(view.total_km)}km / {fmtHours(view.total_hours)}
        </span>
        {/* 数据来源 */}
        {rd.source && (
          <span className="text-[9px] px-1.5 py-0.5 rounded flex-shrink-0"
            style={{ background: "#ECFDF5", color: "#059669" }}
          >
            {rd.source}
          </span>
        )}
        {!isAllLoaded && (
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#A7F3D0", color: "#047857" }}>
            已显示 {visibleCount}/{days.length}天
          </span>
        )}
      </div>

      {/* ★ 备选方案切换器（>=2 条时显示） */}
      {routePlans.length > 1 && (
        <div className="flex items-center gap-1.5 px-3.5 py-2 flex-wrap"
          style={{ background: "#F0FDF4", borderBottom: "1px solid #EEF0F6" }}
        >
          <span className="text-[9px] flex-shrink-0" style={{ color: "#059669" }}>
            <i className="ri-git-branch-line mr-0.5" />方案
          </span>
          {routePlans.map((p, i) => (
            <button
              key={i}
              onClick={() => { setRouteIdx(i); setVisibleCount(INITIAL_SHOW_COUNT); }}
              className="text-[10px] px-2 py-0.5 rounded-full cursor-pointer active:scale-95 transition-all whitespace-nowrap"
              style={{
                background: i === routeIdx ? "linear-gradient(135deg, #059669, #047857)" : "#fff",
                color: i === routeIdx ? "#fff" : "#047857",
                border: `1px solid ${i === routeIdx ? "#047857" : "#A7F3D0"}`,
                fontWeight: i === routeIdx ? 600 : 400,
              }}
            >
              {p.name || `方案${i + 1}`}
              {p.days ? ` · ${p.days}天` : ""}
            </button>
          ))}
        </div>
      )}

      {/* 路线途经点（面包屑导航） */}
      {view.route_path && view.route_path.length > 1 && (
        <div className="flex items-center gap-1 px-3.5 py-2 text-[10px]"
          style={{ background: "#F0FDF4", borderBottom: "1px solid #EEF0F6", overflowX: "auto" }}
        >
          {view.route_path.map((city, i) => (
            <div key={i} className="flex items-center gap-1 flex-shrink-0">
              <span style={{
                color: i === view.route_path!.length - 1 ? "#059669" : "#6B7280",
                fontWeight: i === view.route_path!.length - 1 ? 600 : 400,
              }}>
                {city}
              </span>
              {i < view.route_path!.length - 1 && (
                <i className="ri-arrow-right-s-line" style={{ color: "#A7F3D0", fontSize: 12 }} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* ★ 行程概要（overview / summary / route_info） */}
      {(overview || summary || routeInfo) && (
        <div className="px-3.5 py-2.5 space-y-1.5"
          style={{ background: "#FFF", borderBottom: "1px solid #F0FDF4" }}
        >
          {overview && (
            <div className="flex items-start gap-1.5 text-[11px] leading-relaxed" style={{ color: "#065F46" }}>
              <i className="ri-flag-line flex-shrink-0 mt-0.5" style={{ fontSize: 11, color: "#059669" }} />
              <span><span className="font-semibold">行程概览：</span>{overview}</span>
            </div>
          )}
          {summary && (
            <div className="flex items-start gap-1.5 text-[11px] leading-relaxed" style={{ color: "#4B5563" }}>
              <i className="ri-file-text-line flex-shrink-0 mt-0.5" style={{ fontSize: 11, color: "#6B7280" }} />
              <span><span className="font-semibold">亮点总结：</span>{summary}</span>
            </div>
          )}
          {routeInfo && (
            <div className="flex items-center gap-1.5 flex-wrap text-[9px]">
              {routeInfo.path && (
                <span className="px-1.5 py-0.5 rounded flex items-center gap-0.5"
                  style={{ background: "#ECFDF5", color: "#047857" }}
                >
                  <i className="ri-route-line" style={{ fontSize: 9 }} />{routeInfo.path}
                </span>
              )}
              {routeInfo.total_km != null && (
                <span className="px-1.5 py-0.5 rounded" style={{ background: "#F0FDF4", color: "#059669" }}>
                  {Math.round(routeInfo.total_km)}km
                </span>
              )}
              {routeInfo.total_hours != null && (
                <span className="px-1.5 py-0.5 rounded" style={{ background: "#F0FDF4", color: "#059669" }}>
                  {fmtHours(routeInfo.total_hours)}
                </span>
              )}
              {routeInfo.segments && (
                <span className="px-1.5 py-0.5 rounded truncate"
                  style={{ background: "#F8FAFC", color: "#64748B", maxWidth: "100%" }}
                  title={routeInfo.segments}
                >
                  <i className="ri-road-map-line mr-0.5" style={{ fontSize: 9 }} />{routeInfo.segments}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* 每日行程摘要 */}
      {dailySummary && (
        <div className="px-3.5 py-2 text-[10px] leading-relaxed"
          style={{ background: "#F8FAFC", color: "#64748B", borderBottom: "1px solid #F0FDF4" }}
        >
          <i className="ri-calendar-todo-line mr-1" style={{ fontSize: 10 }} />
          {dailySummary}
        </div>
      )}

      {/* 概要文案 */}
      {rd.message && (
        <div className="px-3.5 py-2 text-[11px] leading-relaxed" style={{ color: "#4B5563", background: "#FFF", borderBottom: "1px solid #F0FDF4" }}>
          {rd.message}
        </div>
      )}

      {/* 每日行程卡片列表 */}
      <div className="flex flex-col">
        {showDays.map((day: DayPlan, idx: number) => (
          <DayPlanCard key={day.day || idx} day={day} idx={idx} isLast={idx === showDays.length - 1 && isAllLoaded} />
        ))}

        {/* 懒加载哨兵 */}
        {!isAllLoaded && (
          <div ref={sentinelRef} className="flex items-center justify-center py-3">
            <div className="flex items-center gap-1.5 text-[10px]" style={{ color: "#AAA" }}>
              <i className="ri-loader-4-line animate-spin" style={{ fontSize: 12 }} />
              <span>向下滑动加载更多...</span>
            </div>
          </div>
        )}

        {/* 全部加载完毕 */}
        {isAllLoaded && days.length > INITIAL_SHOW_COUNT && (
          <div className="flex items-center justify-center py-2" style={{ borderTop: "1px solid #F0F2F8" }}>
            <span className="text-[10px]" style={{ color: "#C0C8D4" }}>
              — 已展示全部 {days.length} 天行程 —
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/** 单日行程卡片子组件 */
function DayPlanCard({ day, idx, isLast }: { day: DayPlan; idx: number; isLast: boolean }) {
  const [expanded, setExpanded] = useState(idx === 0); // 第一天默认展开

  return (
    <div style={{ borderBottom: isLast ? "none" : "1px solid #F0F2F8" }}>
      {/* 日标题栏（可点击展开/收起） */}
      <div
        className="flex items-center justify-between px-3.5 py-2.5 cursor-pointer active:bg-[#F0FDF4] hover:bg-[#F9FAFB]"
        style={{ transition: "background 0.15s" }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2 min-w-0">
          {/* 天数徽章 */}
          <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #059669, #047857)", color: "#fff" }}
          >
            D{day.day || idx + 1}
          </span>
          {/* 日期 */}
          <span className="text-[10px] flex-shrink-0" style={{ color: "#94A3B8" }}>{day.date}</span>
          {/* 主题 */}
          <span className="text-[12px] font-semibold truncate" style={{ color: "#1E293B" }}>{day.theme}</span>
        </div>
        <i className={`ri-${expanded ? "subtract" : "add"}-line flex-shrink-0`} style={{ fontSize: 14, color: "#A7F3D0" }} />
      </div>

      {/* 展开内容 */}
      {expanded && (
        <div className="px-3.5 pb-3 pt-1 space-y-2.5">
          {/* 驾驶信息 */}
          {day.driving_today && (
            <div className="flex items-start gap-1.5 text-[10px]" style={{ color: "#6B7280" }}>
              <i className="ri-steering-line flex-shrink-0 mt-0.5" style={{ color: "#059669", fontSize: 12 }} />
              <span className="leading-relaxed">{day.driving_today}</span>
            </div>
          )}

          {/* 时间轴日程 */}
          {day.schedule?.length > 0 && (
            <div className="space-y-1.5 ml-1 border-l-2 pl-3" style={{ borderColor: "#A7F3D0" }}>
              {day.schedule.map((item, sIdx) => (
                <div key={sIdx} className="flex items-start gap-2 relative">
                  {/* 时间轴圆点 */}
                  <div className="absolute left-[-18px] top-1 w-2.5 h-2.5 rounded-full border-2" style={{ borderColor: "#059669", background: "#FFF" }} />
                  <span className="text-[10px] font-semibold font-mono flex-shrink-0 w-10" style={{ color: "#059669" }}>
                    {item.time}
                  </span>
                  <span className="text-[11px] leading-relaxed flex-1 min-w-0" style={{ color: "#374151" }}>
                    {item.activity}
                    {/* ★ 日程备注 */}
                    {item.note && (
                      <span className="block text-[9px] mt-0.5" style={{ color: "#94A3B8" }}>
                        <i className="ri-chat-1-line mr-0.5" style={{ fontSize: 9 }} />{item.note}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* 用餐推荐 */}
          {day.meals?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {day.meals.map((meal, mIdx) => (
                <span key={mIdx} className="inline-flex items-center gap-1 text-[9px] px-2 py-1 rounded-lg"
                  style={{ background: "#FEF3C7", color: "#92400E", maxWidth: "100%" }}
                >
                  <i className={`ri-${meal.type.includes("早") ? "sunrise" : meal.type.includes("午") ? "sun" : "moon"}-line`} style={{ fontSize: 10 }} />
                  {/* ★ 用餐时间 */}
                  {meal.time && <span className="font-mono flex-shrink-0" style={{ color: "#B45309" }}>{meal.time}</span>}
                  <span className="truncate max-w-[180px]">{meal.suggest}</span>
                </span>
              ))}
            </div>
          )}

          {/* 住宿（含价格与推荐理由） */}
          {day.hotel?.name && (
            <div className="flex items-start gap-1.5 text-[10px]" style={{ color: "#4B5563" }}>
              <i className="ri-hotel-bed-line flex-shrink-0 mt-0.5" style={{ color: "#8B5CF6", fontSize: 12 }} />
              <span className="leading-relaxed flex-1 min-w-0">
                <span className="font-medium">住宿：</span>{day.hotel.name}
                {/* ★ 酒店价格 */}
                {day.hotel.price && (
                  <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-semibold align-middle"
                    style={{ background: "#F3E8FF", color: "#7C3AED" }}
                  >
                    ¥{day.hotel.price}
                  </span>
                )}
                {day.hotel.reason && ` — ${day.hotel.reason}`}
              </span>
            </div>
          )}

          {/* 温馨提示 */}
          {day.tips && (
            <div className="rounded-md p-2 text-[10px] leading-relaxed" style={{ background: "#EFF6FF", color: "#1E40AF", borderLeft: "3px solid #3B82F6" }}>
              <i className="ri-lightbulb-line mr-1" style={{ fontSize: 11 }} />
              {day.tips}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
